import React, { useEffect, useRef, useState } from 'react';
import JBButton from '@jbhi-fi/lanyard-ui/components/Button';
import debounce from 'lodash/debounce';
import { TitleSection } from '../TitleSection/TitleSection';
import { useShopifyProduct } from '../../../../api/shopify/ShopifyProduct';
import { KeyFeatures } from '../productMeta/KeyFeatures';
import Price from '../price/Price';
import Variants from '../variants/Variants';
import LocationSvg from '../../../../../assets/svg/icon-store-finder.svg';
import AvailabilityStatus from '../availabilityStatus/AvailabilityStatus';
import { StickyCallToAction } from '../callToAction/StickyCallToAction';
import { CallToAction } from '../callToAction/CallToAction';
import ReleaseDate from '../productMeta/ReleaseDate';
import BuyNowPayLater from '../buyNowPayLater/BuyNowPayLater';
import Messages from '../messages/Messages';
import AskForADeal from '../askForADeal/AskForADeal';
import MessageItem, { Message } from '../messages/Message';
import { Images } from '../images/Images';
import ExtraDescription from './../productMeta/ExtraDescription';
import { isSoftware, useMicrosoft365Props } from '../utils/utils';
import { isFirefoxStrictModeEnabled } from '@jbhi-fi/utils/src/firefoxStrictMode';
import PdpSchema from '../schema/PdpSchema';
import AccordionLinks from '../accordionLinks/AccordionLinks';
import { getElementPadding } from '../utils/utils';
import ProductService from '../productService/ProductService';
import { removePTags } from '../utils/utils';
import { useIdPrefix } from '@jbhi-fi/components/src/IdPrefix';
import { QuantitySelect } from '@jbhi-fi/components/src/QuantitySelect/QuantitySelect';
import AvailabilityWrapper from '../../availability/availability-wrapper/AvailabilityWrapper';
import { ProductFulfilmentInfoWrapped } from '../../localisation/components/product-fulfilment-info/ProductFulfilmentInfoWrapped';
import { AlgoliaApiClient } from '../../../../api/localisation/AlgoliaApiClient';
import { parseAlgoliaStoreData } from '../../localisation/utils/parseAlgoliaStoreData';
import { StoreLocation } from '../../localisation/utils/types';
import { MovieRatingType } from '../types';
import { getIsServicesEnabled } from '../../../../utils/getIsServicesEnabled';
import { useLocalisationContext } from '../../localisation/context/LocalisationContext';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import {
  getIsPdpLocalisationEnabled,
  getShouldShowLocalisationDisplayModelStatus,
} from '../../localisation/utils/productFulfilmentUtils';
import { MovieRating } from '@jbhi-fi/components/src/ProductDetail/ProductDescriptions/MovieRating';
import { ProductCompareBar } from '@jbhi-fi/components/src/ProductCompare/ProductCompareBar/ProductCompareBar';
import { SpecialOffers } from '@jbhi-fi/components/src/ProductDetail/SpecialOffers';
import {
  fetchServiceSuggestions,
  getSelectedServiceSuggestion,
  removePDPServiceSelection,
  removeSelectedServiceSuggestion,
} from '../utils/product-service';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag/useFeatureFlag';
import { transformShopifyProductForTracking } from '../../../../utils/cart/transformShopifyProductForTracking';
import { getStoreCurrency } from '../../../../utils/shopify/getStoreCurrency';
import { PerksPromoAlert } from '@jbhi-fi/components/src/PerksPromoAlert/PerksPromoAlert';
import { getListNamesForSku, removeListNameForSku } from '@jbhi-fi/components/src/ProductCard/utils/listTracking';
import { STORE_LOCATIONS } from '../../../../utils/location/locationHelper';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { dimensionsFilter } from '../accordionContent/DimensionsContent';
import { getPromoTags } from '@jbhi-fi/components/src/ProductCard/utils/getTags';
import { ShopifyProductTransformer } from '@jbhi-fi/components/src/Shopify/transformers/ShopifyProductTransformer';
import { useServiceSuggestions } from '@jbhi-fi/components/src/hooks/useServiceSuggestions';
import { addMarketplaceTrackingValuesToEcommerceCartItem } from '../../../../utils/event-tracking/globalTrackingHelpers';
import { InstallationOffer } from '../installationOffer/InstallationOffer';
import { ProductMessages } from '@jbhi-fi/components/src/ProductDetail/ProductMessages';
import { useProducts } from '@jbhi-fi/components/src/hooks/useProducts/useProducts';
import { DigitalProductBlock } from '@jbhi-fi/components/src/Attach/DigitalProductBlock/DigitalProductBlock';
import { hideZendeskDepartment } from '@jbhi-fi/utils/src/zendesk/zendesk';
import ProductConditionInfo, { Condition } from '../productCondition/ProductCondtionInfo';
import { sendInsiderObject } from '../utils/insider';
import { CouponsContainer } from '@jbhi-fi/components/src/CouponsContainer';
import { BnplOption } from '@jbhi-fi/components/src/ProductDetail/BuyNowPayLater/types';
import { Tracker } from '@jbhi-fi/components/src/Tracking';
import { sumOfItemPrice } from '@jbhi-fi/utils/src/ga4Tracking/ga4utils';
import { DEFAULT_CONTAINER_TITLE } from '@jbhi-fi/components/src/CouponsContainer/constants';
import { getDeliveryTag } from '@jbhi-fi/utils/src/getDeliveryTag/getDeliveryTag';
import { styles } from './RightSide.css';
import { serviceHelper } from '../../../../config/service-helper';
import { useCouponContext } from '../../../../contexts/CouponContextProvider';
import { InjectedCustomLink } from '../injectedCustomLink/InjectedCustomLink';
import { useAlternativesContext } from '@jbhi-fi/components/src/ProductAlternatives/AlternativesContextProvider';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { Icon } from '@jbhi-fi/lanyard-ui/components/Icon';
import ProductBundle from '../smartModules/ProductBundle';
import { SmartModuleWrapper } from '../smartModules/SmartModuleWrapper';

const RightSide = (): JSX.Element => {
  const searchParams = new URLSearchParams(window.location.search);
  const { isLocalisationEnabled } = useLocalisationContext();
  const storeId = searchParams.get('store');
  const rightSideRef = useRef<HTMLDivElement | null>(null);
  const headerElementRef = useRef(document.querySelector('#shopify-section-header'));
  const {
    shopify,
    metafields,
    productMetafields,
    marketplaceSellers,
    isHideAwaitingStock,
    isAwaitingStock,
    productMessages,
    specialOffers,
    installationMessages,
  } = useShopifyProduct();
  const {
    Display,
    Product,
    Id: variantSku,
    ProductTags,
    Category,
    Department,
    ProductGroup,
    Button: buttonType,
    Availability: availability,
    Price: productPrice,
    ShowWasPrice: shouldShowWasPrice,
    ShowPerksPrice,
    DeliveryTag,
  } = productMetafields;
  const productGroup = ProductGroup?.Id;
  const isPdpLocalisationEnabled = getIsPdpLocalisationEnabled({
    productGroup,
    isLocalisationEnabled,
    excludedProductGroups: window.Jbhifi?.localisationSettings?.excludedProductGroups ?? '',
  });
  const [quantity, setQuantity] = useState(1);
  const [stickyEnabled, setStickyEnabled] = useState(false);
  const isStickyEnabled = useRef(stickyEnabled);
  const [isAvailabilityWrapperOpen, setIsAvailabilityWrapperOpen] = useState(
    isPdpLocalisationEnabled ? false : Boolean(storeId)
  );
  const [activeAvailabilityVariant, setActiveAvailabilityVariant] = useState<'delivery' | 'default' | 'inStore'>(
    storeId && isPdpLocalisationEnabled ? 'inStore' : 'default'
  );
  const [LIAStore, setLIAStore] = useState<StoreLocation>();
  const storeLocation = window.Jbhifi?.storeLocation;
  const [isCtaVisible, setIsCtaVisible] = useState(true);
  const variantAvailable = shopify.variants[0].available;
  const isDigital = availability?.Fulfilment === 'Digital';
  const [priceTagWidth, setPriceTagWidth] = useState(0);
  const sku = shopify?.variants[0]?.sku ? parseInt(shopify?.variants[0]?.sku) : 0;
  const title = shopify?.title;

  const customerWishlist = window.Jbhifi.customerWishlist;
  const freightPrice = Product?.FreightPrice;
  const isMarketplaceProduct =
    ShopifyProductTransformer.isMarketplaceProduct({ product: shopify }) && Boolean(marketplaceSellers?.[0]);

  const deliveryTag = getDeliveryTag(DeliveryTag, freightPrice);

  const features = Display?.KeyFeatures;
  const brand = productMetafields?.Product?.Brand;
  const preOrder: boolean = Product?.Flags?.some(flag => flag.Name === 'Pre Order');
  const preOrderClickAndCollect: boolean = Product?.Flags?.some(flag => flag.Name === 'Pre Order Click & Collect');
  const preOrderDeliveryMessage =
    window?.Jbhifi?.pdpAlerts && window?.Jbhifi?.pdpAlerts?.preOrderDeliveryMessage
      ? window?.Jbhifi?.pdpAlerts?.preOrderDeliveryMessage
      : null;
  const isPreOrder: boolean = preOrder || preOrderClickAndCollect;
  const isComingSoon = Product?.Flags?.some(flag => flag.Name === 'Coming Soon');
  const lrpExists = availability.LimitedRun ? true : false;
  const isDigitalPreOrder =
    availability?.Fulfilment === 'Digital' && availability?.AvailabilityStatement === 'Pre-order online';
  const isTemporarilyUnavailableOverride: boolean = Product?.Flags?.some(
    o => o.Name === 'Temporarily Unavailable Override'
  );
  const limitedRunPromo = availability?.LimitedRun && availability.LimitedRun;
  const numberOfReviews = Display?.NumberOfProductReviews ?? 0;
  const averageReviews = Display?.AverageProductReviewRating;
  const artist = (Display?.Preamble ? Display.Preamble : Display.Artist ?? '').toLowerCase();
  const isBackOrder: boolean = availability?.AvailabilityStatement === 'Back order available';
  const imsImportantMessage = productMessages?.some(message => message.type === 'Important')
    ? productMessages.find(message => message.type === 'Important')?.content
    : null;
  const gtmData = metafields?.google_tag_manager;
  const currentDate = new Date();
  const releaseDateUnformatted = new Date(Product?.ReleaseDate);
  const timeZone = storeLocation === STORE_LOCATIONS.NZ ? 'Pacific/Auckland' : 'Australia/Melbourne';

  const releaseDateDay = new Date(Product?.ReleaseDate).toLocaleDateString('en-us', {
    day: 'numeric',
    timeZone: timeZone,
  });
  const releaseDateMonth = new Date(Product?.ReleaseDate).toLocaleDateString('en-us', {
    month: 'short',
    timeZone: timeZone,
  });
  const releaseDateYear = new Date(Product?.ReleaseDate)
    .toLocaleDateString('en-us', {
      year: 'numeric',
      timeZone: timeZone,
    })
    .substr(-2);
  const releaseDateFormatted = `${releaseDateDay} ${releaseDateMonth} ${releaseDateYear}`;
  const releaseDateDisplay = Product?.ReleaseDateText ? Product?.ReleaseDateText : releaseDateFormatted;
  const productCartData = {
    ...productMetafields,
    availabilityFulfilment: availability?.Fulfilment,
    availabilityCanPreOrder: availability?.CanPreOrder,
    freightPrice,
    id: shopify.variants[0]?.id?.toString() ?? '',
    maxQty: availability?.Fulfilment === 'Digital' ? 1 : Product?.LimitPerOrder,
    quantity,
    sku: sku.toString(),
  };
  const hasProductOverview = Boolean(shopify?.description);
  const hasTermsOfService = Boolean(Display?.TermsAndConditions);
  const dimensionsProductGroupIds = window.Jbhifi.dimensionsProductGroupIds?.split(',');
  const productGroupId = ProductGroup?.Id?.toString();
  const hasDimensions = productMetafields.Display.SpecificationDetails?.some(dimensionsFilter);
  const shouldShowDimensions = dimensionsProductGroupIds?.includes(productGroupId) && hasDimensions;
  const overallStatus = availability?.OverallStatus;
  const highlight = Display && Display?.Highlight ? Display?.Highlight : undefined;
  const softwareFormat = Display?.Format;
  const consumerAdvice = Display?.SpecificationDetails?.find(x => x.Name === 'Consumer Advice')?.Values[0] ?? null;
  const ageRating = Display?.SpecificationDetails?.find(x => x.Name === 'Rating')?.Values[0] ?? null;
  const movieRating = window.Jbhifi.movieRatingsSettings.find(
    (rating: any) => rating.ratingName === ageRating
  ) as MovieRatingType;
  const shouldShowMovieRating = storeLocation === 'NZ' && ageRating && movieRating;
  const pdpDepartmentArtistIds = window.Jbhifi.pdpDepartmentArtistIds.split(',');
  const pdpDepartmentTitleIds = window.Jbhifi.pdpDepartmentTitleIds.split(',');
  const isTemporarilyUnavailable: boolean = ['Temporarily unavailable', 'Temporarily unavailable online'].includes(
    availability?.AvailabilityStatement
  );
  const isUnavailable =
    isTemporarilyUnavailable || overallStatus === 'NoLongerAvailable' || overallStatus === 'NotAvailable';
  const rating = Display?.AverageProductReviewRating
    ? Math.round(Number(Display?.AverageProductReviewRating) * 1.0 * 10) / 10
    : 0;
  const pctRating = (Number(rating) / 5) * 100;
  const productAlerts: Array<Message> = [];
  const requiresExtraDescriptions = Department?.Id === 110 || Department?.Id === 92 ? true : false;
  const isReleaseDateInPast = releaseDateUnformatted < currentDate ? true : false;
  const pdpCategory = Category && Category?.Name ? Category?.Name : null;
  const gtin = shopify?.id;
  const schemaTitle = window?.Jbhifi?.product_title;
  const productDescription = shopify?.description && shopify?.description.length > 0 ? shopify?.description : null;
  const handle = shopify.handle ? shopify.handle : null;
  const productFeaturedImage = shopify.featured_image ? shopify.featured_image : null;
  const currentlyAvailable = !isDigital && buttonType !== 'DoNotDisplay' && buttonType !== 'NotifyMe';
  const isLoggedIn: boolean = window.theme.isCustomerLoggedIn;
  const hideStrikeThrough = Product?.Flags?.some(flag => flag.Name === 'Hide Strikethrough');
  const isInStoreOnly =
    ProductTags &&
    ProductTags?.AllCalculatedProductTags &&
    ProductTags?.AllCalculatedProductTags?.includes('In Store Only');
  const checkAvailabilityId = useIdPrefix('checkavailability-cta');
  const isDesktop = useBreakpoint('lg', 'up');
  const isMobile = !useBreakpoint('md', 'up');
  const isTablet = !isDesktop && !isMobile;
  const shouldCtaRender =
    buttonType !== 'DoNotDisplay' &&
    (variantAvailable ||
      ((buttonType === 'NotifyMe' || buttonType === 'CheckAvailability') &&
        buttonType &&
        !isFirefoxStrictModeEnabled())) &&
    !isMobile;
  const shouldAvailabilityRender =
    ((buttonType !== 'DoNotDisplay' && variantAvailable && !isDigital) || isInStoreOnly) && isPdpLocalisationEnabled;
  const isServicesEnabled = getIsServicesEnabled();
  const isJbSalesLineEnabled = window.Jbhifi?.jbSalesLineEnabled;
  const shouldShowSalesLineNumber = window.Jbhifi?.shouldShowjbSalesLineOnPdp ?? true;
  const isServicesPDPHidden = useFeatureFlag('isServicesPDPHidden');
  const isPdpQuantityEnabled = useFeatureFlag('isPdpQuantityEnabled');
  const shouldShowQuantitySelect = Boolean(isPdpQuantityEnabled && productCartData?.maxQty !== 1);
  const shouldShowDisplayModelStatus = getShouldShowLocalisationDisplayModelStatus({
    productGroup: productMetafields?.ProductGroup?.Id,
    displayModelProductGroups: window?.Jbhifi?.localisationSettings?.displayModelProductGroups ?? '',
  });

  const { handleViewAlternatives } = useAlternativesContext();
  const isSimilarItemsEnabled =
    window.smartModuleSettings.isPdpSimilarItemsEnabled && !!window.smartModuleSettings?.alternativesGoogleRecModelType;

  const shouldFetchServiceSuggestions =
    (productMetafields?.Product?.EligibleForServices ||
      productMetafields?.Product?.EligibleForServices === undefined) &&
    !isMarketplaceProduct;

  const variants = [{ Sku: sku, DisplayPriceInc: shopify?.price / 100 }];

  const { serviceSuggestions } = useServiceSuggestions({
    variants,
    shouldFetchServiceSuggestions,
    fetchServiceSuggestions,
  });

  const isEarlyAccess = !!productPrice?.PerksPricing?.PromoNamesApplied?.find(status => status.includes('JBPEAR'));
  const isExclusiveDeal = !!productPrice?.PerksPricing?.PromoNamesApplied?.find(status => status.includes('JBPEXC'));
  const hasPerksPromotion = isEarlyAccess || isExclusiveDeal;
  const { isPerks } = useCustomerContext();
  const displayPriceAfterCashback = productMetafields.Price?.DisplayPriceAfterCashback;
  const price = displayPriceAfterCashback ? productPrice?.PriceAfterCashback : shopify?.price / 100;
  const wasPrice = displayPriceAfterCashback ? productPrice?.DisplayPriceInc : productPrice?.WasPrice;
  const bnplOptions = window.Jbhifi?.pdpBnpl?.options;
  const bnplPrice = hasPerksPromotion && isPerks ? productPrice?.PerksPricing.DisplayPriceInc : shopify?.price / 100;
  const showBnpl = !!bnplOptions?.some(
    (option: BnplOption) => option.enabled && bnplPrice >= option.lowerLimit && bnplPrice <= option.upperLimit
  );
  const bannerLabel =
    isPerks || !isEarlyAccess || (isEarlyAccess && isExclusiveDeal) ? ProductTags?.BannerTag?.Label : undefined;
  const priceTagBannerLabel = ProductTags?.PriceBannerTag?.Label;
  const marketplaceSeller = ShopifyProductTransformer.toMarketplaceSeller({
    product: shopify,
    marketplaceSellers,
    onlineProduct: productMetafields,
  });
  const [trackingListName, setTrackingListName] = useState<string | undefined | null>(null);

  const askForADealTopIds = window.Jbhifi.askForADealPdpProductGroupIdsTop.split(',').map(id => id.trim());
  const askForADealHideIds = window.Jbhifi.askForADealPdpProductGroupIdsHide.split(',').map(id => id.trim());

  const shouldShowAskForADeal =
    !askForADealHideIds.includes(productGroupId ?? '') &&
    isJbSalesLineEnabled &&
    currentlyAvailable &&
    !isMarketplaceProduct &&
    (isInStoreOnly || (!lrpExists && !isDigital && !isInStoreOnly));

  const askForADealPosition: 'top' | 'bottom' = askForADealTopIds.includes(productGroupId ?? '') ? 'top' : 'bottom';
  const microsoft365AttachMessaging = window.Jbhifi.microsoftAttachMessaging;
  const attachProductGroupIds = microsoft365AttachMessaging?.productGroupIds.split(',');
  const shouldShowMicrosoft365Attach =
    microsoft365AttachMessaging?.isMicrosoftAttachEnabled &&
    attachProductGroupIds?.some(id => id === productGroupId?.toString());
  const { products: [microsoft365Product, algoliaProduct] = [] } = useProducts({
    productSkus: [microsoft365AttachMessaging?.attachSku.toString() ?? '', sku.toString()],
    isEnabled: shouldShowMicrosoft365Attach || (!isDesktop && isSimilarItemsEnabled),
  });
  const ms365props = useMicrosoft365Props(microsoft365Product);
  const disclaimers = window.Jbhifi.disclaimers ?? [];

  const productConditionCode = Product?.Condition?.Code ?? null;

  const productConditionInfo = window.Jbhifi?.pdpConditionInfo?.info?.find(info =>
    info?.productGroups?.includes(productGroupId ?? '')
  );
  const isProductConditionEnabled = window.Jbhifi?.pdpConditionInfo?.enabled && productConditionInfo;
  const productConditionIndex =
    productConditionCode &&
    productConditionInfo?.conditions?.findIndex(
      (condition: Condition) => condition?.conditionCode?.toString() === productConditionCode.toString()
    );

  const imsReviewsEnabled = Display.ShowReviews;
  const themeReviewsEnabled = window.Jbhifi?.pdpReviewsEnabled;
  const showReviews =
    imsReviewsEnabled &&
    (!isDigital || availability?.ProductLifecycle !== 'PreOrder') &&
    (themeReviewsEnabled || !imsReviewsEnabled || numberOfReviews >= 1);

  const {
    isCouponSurfacingEnabled,
    couponsWithExclusions,
    handleSelectCoupon,
    isFetched: isCouponsFetched,
    isActive: isCouponsActive,
  } = useCouponContext({
    location: 'pdp-coupon',
    product: {
      departmentCode: Department.Id.toString(),
      sku: sku.toString(),
      productGroupId: productGroupId || '',
      supplierNumber: Product?.SupplierProductDetails?.[0]?.SupplierNumber.toString(),
      isPreOrder: isPreOrder,
      isMarketplace: isMarketplaceProduct,
    },
  });

  const shouldShowCoupons = isCouponSurfacingEnabled && isPerks && (couponsWithExclusions?.length ?? 0) > 0;
  const hideButton = buttonType === 'DoNotDisplay';
  const doNotDisplayAndInStoreOnly = hideButton && isInStoreOnly;
  const doNotDisplayAndNotInStoreOnly = hideButton && !isInStoreOnly;
  const wishlistButtonVariant =
    hideButton || isInStoreOnly || (!variantAvailable && buttonType !== 'NotifyMe') || isDigital;

  const viewedItem = addMarketplaceTrackingValuesToEcommerceCartItem({
    cartItem: transformShopifyProductForTracking({
      item: productMetafields,
      isAvailable: variantAvailable,
      listName: trackingListName || 'pdp',
    }),
    product: shopify,
    onlineProduct: productMetafields,
    marketplaceSellers,
  });

  const pdpTopSmartModuleSettings = window.smartModuleSettings.pdpTop;

  useEffect(() => {
    sendInsiderObject(productMetafields, shopify);
  }, [productMetafields.Id]);

  useEffect(() => {
    const trackingListName = getListNamesForSku(sku.toString());
    setTrackingListName(trackingListName);
    removeListNameForSku(sku.toString());
  }, []);

  useEffect(() => {
    if ((isCouponsActive && isCouponsFetched) || !isCouponsActive) {
      Tracker.trackCustomEvent({
        event: 'view_item',
        location: 'pdp',
        value: sumOfItemPrice(viewedItem),
        ecommerce: {
          currency: getStoreCurrency(),
          items: Array.isArray(viewedItem) ? viewedItem : [viewedItem],
        },
        ...(couponsWithExclusions?.length
          ? {
              coupon_name: couponsWithExclusions?.map(coupon => coupon.content?.displayName).join(' | ') || '',
              coupon: couponsWithExclusions?.map(coupon => coupon.couponName).join(' | ') || '',
              coupon_id: couponsWithExclusions?.map(coupon => coupon.couponExternalId).join(' | ') || '',
              coupon_count: couponsWithExclusions?.length || 0,
            }
          : undefined),
      });
    }
  }, [isCouponsFetched]);

  const getItemLabel = () => {
    if (isInStoreOnly) return 'In-store only';
    if (isComingSoon) return 'Coming soon';
    return 'Out of stock';
  };
  let tagHideBackOrder = window.Jbhifi.pdpAlerts.hideBackOrder;
  let tagHidePreOrder = false;
  let tagHideTemporarilyUnavailable = false;

  shopify.tags.forEach((tag: string) => {
    if (tag === 'Hide:BackOrderAlert') {
      tagHideBackOrder = true;
    } else if (tag === 'Hide:PreOrderAlert') {
      tagHidePreOrder = true;
    } else if (tag === 'Hide:TemporarilyUnavailableAlert') {
      tagHideTemporarilyUnavailable = true;
    }
  });

  // Temporarily unavailable or Temporarily unavailable override alert
  if (!tagHideTemporarilyUnavailable && (isTemporarilyUnavailableOverride || isTemporarilyUnavailable)) {
    productAlerts.unshift({
      Message: window?.Jbhifi?.pdpAlerts?.temporarilyUnavailable || null,
      MessageType: 'Critical',
    });
  }

  // LRP Out of stock alert
  if (limitedRunPromo && limitedRunPromo.StockLevelStatus === 'Gone') {
    productAlerts.unshift({
      Message: window?.Jbhifi?.pdpAlerts?.soldOut || null,
      MessageType: 'Critical',
    });
  }

  // Back-order alert
  if (!tagHideBackOrder && isBackOrder && availability?.DeliveryConstraint !== 'BulkyDeliveryZones') {
    productAlerts.push({
      Message: window?.Jbhifi?.pdpAlerts?.backOrder || null,
      MessageType: 'General',
    });
  }

  // Bulky back-order alert
  if (!tagHideBackOrder && isBackOrder && availability?.DeliveryConstraint === 'BulkyDeliveryZones') {
    productAlerts.push({
      Message: window?.Jbhifi?.pdpAlerts?.bulkyBackOrder || null,
      MessageType: 'General',
    });
  }

  // Awaiting stock
  if ((isAwaitingStock || isPreOrder) && isHideAwaitingStock && !!imsImportantMessage) {
    productAlerts.push({
      Message: imsImportantMessage.split('\n').length > 1 ? imsImportantMessage.split('\n')[1] : imsImportantMessage,
      MessageType: 'General',
      MessageHeading: imsImportantMessage.split('\n').length > 1 ? removePTags(imsImportantMessage.split('\n')[0]) : '',
    });
  }

  // Pre-order alert1`
  let preOrderAlert;
  if (!tagHidePreOrder && isPreOrder) {
    preOrderAlert = {
      Message: window?.Jbhifi?.pdpAlerts?.preOrder || null,
      MessageType: 'Neutral',
    };
  }

  const handleResize = () => {
    let isSticky = true;
    const currentHeight = rightSideRef.current?.clientHeight ? rightSideRef.current?.clientHeight + 200 : 0;
    if (currentHeight > window.innerHeight || window.innerWidth < 1024) {
      setStickyEnabled(false);
      isSticky = false;
    } else {
      setStickyEnabled(true);
    }

    if (isSticky) {
      handleHeaderChange();
    }

    document.documentElement.style.setProperty(
      '--scrollbar-width',
      `${window.innerWidth - document.documentElement.clientWidth}px`
    );
  };

  const handleHeaderChange = () => {
    if (headerElementRef.current && isStickyEnabled.current && rightSideRef.current) {
      const isPinned = headerElementRef.current.classList.contains('headroom--pinned');
      const isTop = headerElementRef.current.classList.contains('headroom--top');

      if ((isPinned && !isTop) || isTop) {
        rightSideRef.current.style.top = `calc(var(--pdp-rightside-top) + ${headerElementRef.current.clientHeight}px)`;
      } else {
        rightSideRef.current.style.top = 'var(--pdp-rightside-top)';
      }
    }
  };

  useEffect(() => {
    if (!headerElementRef.current) return;
    const resizeHandler = debounce(handleResize, 0);

    // Init resize handler on mount to set sticky state
    resizeHandler();
    window.addEventListener('resize', resizeHandler);

    const observer = new MutationObserver(handleHeaderChange);
    observer.observe(headerElementRef.current, {
      attributeFilter: ['class'],
    });

    return () => {
      window.removeEventListener('resize', resizeHandler);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    isStickyEnabled.current = stickyEnabled;
    if (!rightSideRef.current) return;
    if (stickyEnabled) {
      handleHeaderChange();
    } else {
      rightSideRef.current.style.top = '0';
    }
  }, [stickyEnabled]);

  useEffect(() => {
    if (rightSideRef.current && rightSideRef.current.clientWidth > 0) {
      const rightSidePadding = getElementPadding(rightSideRef.current);
      setPriceTagWidth(rightSideRef.current.clientWidth - rightSidePadding);
    }
  }, [rightSideRef.current]);

  useEffect(() => {
    const getStore = async () => {
      setLIAStore(parseAlgoliaStoreData(await AlgoliaApiClient.getStore(storeId?.toString() ?? '')));
    };
    if (storeId) {
      getStore();
    }
    const selectedSuggestion = getSelectedServiceSuggestion();
    if (selectedSuggestion?.productSku !== sku) {
      removePDPServiceSelection();
      removeSelectedServiceSuggestion();
    }
  }, []);

  useEffect(() => {
    const askForADealDepartmentId = window.Jbhifi?.zendeskDepartmentIds?.askForADeal;
    if (!isMarketplaceProduct || !askForADealDepartmentId) return;

    (async () => {
      await hideZendeskDepartment(askForADealDepartmentId);
    })();
  }, [isMarketplaceProduct]);

  useEffect(() => {
    const hasCheckAvailabilityLocalStorage = window.localStorage.getItem('openAvailabilityModal');
    if (hasCheckAvailabilityLocalStorage) {
      checkAvailabilityAction();
      window.localStorage.removeItem('openAvailabilityModal');
    }
  }, []);

  const handleTrackClickCta = (label: string) => {
    Tracker.trackCustomEvent({ event: 'click_cta', link_text: label, location: 'pdp-coupon' });
  };

  const checkAvailabilityAction = () => {
    setIsAvailabilityWrapperOpen(true);
    setActiveAvailabilityVariant('inStore');
  };

  return (
    <div ref={rightSideRef} className={[styles.root, stickyEnabled ? styles.stickyEnabled : null].join(' ')}>
      <PdpSchema
        sku={sku}
        price={price}
        pdpCategory={pdpCategory}
        schemaTitle={schemaTitle}
        gtin={gtin}
        brand={brand}
        numberOfReviews={numberOfReviews}
        averageReviews={averageReviews}
        handle={handle}
        productFeaturedImage={productFeaturedImage}
        productDescription={productDescription}
        highlight={highlight}
        availabilityStatement={availability?.AvailabilityStatement}
      />
      <TitleSection
        className={styles.title}
        plu={Product.Barcode}
        code={Product?.Model}
        sku={sku.toString()}
        title={title}
        bannerLabel={bannerLabel}
        numberOfReviews={numberOfReviews}
        averageReviews={averageReviews}
        logo={Display?.BrandImageUrl}
        brand={brand}
        availability={!!availability}
        preOrder={isPreOrder}
        isComingSoon={isComingSoon}
        isDigitalPreOrder={isDigitalPreOrder}
        artist={artist}
        productDepartment={Department?.Id}
        pctRating={pctRating}
        rating={rating}
        showReviews={showReviews}
        pdpDepartmentArtistIds={pdpDepartmentArtistIds}
        pdpDepartmentTitleIds={pdpDepartmentTitleIds}
        softwareFormat={softwareFormat}
        limitedRunPromo={limitedRunPromo}
        isPerksMember={isPerks}
        marketplaceSeller={ShopifyProductTransformer.toMarketplaceSellerCalloutProps({
          product: shopify,
          marketplaceSellers,
          onlineProduct: productMetafields,
        })}
        promoTags={getPromoTags(ProductTags?.PromoTags)}
      />
      {!isDesktop && isSimilarItemsEnabled ? (
        <div className={styles.similarItemsButtonContainer}>
          <Button
            classes={styles.similarItemsButton}
            onClick={() => handleViewAlternatives(algoliaProduct, 'similarItems')}
            type="linkWithIcon"
            label="Similar items"
            icon={<Icon name="ArrowRight" />}
          />
        </div>
      ) : null}

      {!isDesktop ? <Images /> : null}

      {isDesktop ? (
        <>
          <ProductCompareBar />

          {isCtaVisible ? (
            <Price
              className={styles.title}
              hideStrikeThrough={hideStrikeThrough}
              deliveryTag={deliveryTag}
              variantAvailable={variantAvailable}
              isDigital={isDigital}
              isPerksMember={isPerks}
              pricing={productPrice}
              shouldShowWasPrice={shouldShowWasPrice}
              showPerksPrice={ShowPerksPrice}
              toolTips={disclaimers}
              bannerLabel={priceTagBannerLabel}
            />
          ) : null}
          {showBnpl ? <BuyNowPayLater options={bnplOptions} productPrice={bnplPrice} /> : null}

          {hasPerksPromotion || isCouponSurfacingEnabled ? (
            <PerksPromoAlert
              isLoggedIn={isLoggedIn}
              isPerksMember={isPerks}
              isEarlyAccess={isEarlyAccess}
              isExclusiveDeal={isExclusiveDeal}
              isCouponSurfacingEnabled={isCouponSurfacingEnabled}
              messages={window.Jbhifi?.perksPromoAlerts}
              onLogin={() => handleTrackClickCta('Perks login')}
              b2cBaseUrl={serviceHelper.b2cBaseUrl}
            />
          ) : null}
        </>
      ) : null}

      {isMobile ? (
        <CallToAction
          shouldCtaRender={shouldCtaRender}
          buttonType={buttonType}
          variantAvailable={variantAvailable}
          productCartData={productCartData}
          product={shopify}
          isDigital={isDigital}
          setIsCtaVisible={setIsCtaVisible}
          productMetafields={productMetafields}
          sku={sku}
          variantSku={variantSku}
          priceTagWidth={priceTagWidth}
          gtmData={gtmData}
          trackingListName={trackingListName}
          productCategoryHierarchy={Display?.CategoryHierarchy}
          quantity={quantity}
          checkAvailabilityAction={checkAvailabilityAction}
        />
      ) : null}

      {isProductConditionEnabled && productConditionIndex != null && productConditionIndex > -1 ? (
        <ProductConditionInfo
          info={productConditionInfo}
          productConditionIndex={productConditionIndex}
          classNames={styles.messages}
        />
      ) : null}

      <Variants coupons={isCouponsFetched ? couponsWithExclusions : []} hasQuantitySelect={shouldShowQuantitySelect} />

      {shouldShowQuantitySelect ? (
        <QuantitySelect maxQty={productCartData?.maxQty} onChange={setQuantity} quantity={quantity} label="Quantity" />
      ) : null}

      {isDesktop ? (
        <CallToAction
          shouldCtaRender={shouldCtaRender}
          buttonType={buttonType}
          variantAvailable={variantAvailable}
          productCartData={productCartData}
          product={shopify}
          isDigital={isDigital}
          setIsCtaVisible={setIsCtaVisible}
          productMetafields={productMetafields}
          sku={sku}
          variantSku={variantSku}
          priceTagWidth={priceTagWidth}
          gtmData={gtmData}
          trackingListName={trackingListName}
          productCategoryHierarchy={Display?.CategoryHierarchy}
          quantity={quantity}
          checkAvailabilityAction={checkAvailabilityAction}
        />
      ) : null}

      {isTablet ? (
        <>
          <ProductCompareBar />
          {isCtaVisible ? (
            <Price
              className={styles.title}
              hideStrikeThrough={hideStrikeThrough}
              deliveryTag={deliveryTag}
              variantAvailable={variantAvailable}
              isDigital={isDigital}
              fullWidth
              pricing={productPrice}
              shouldShowWasPrice={shouldShowWasPrice}
              showPerksPrice={ShowPerksPrice}
              toolTips={disclaimers}
              bannerLabel={priceTagBannerLabel}
            />
          ) : null}

          {showBnpl && price ? <BuyNowPayLater options={bnplOptions} productPrice={price} /> : null}

          <CallToAction
            shouldCtaRender={shouldCtaRender}
            buttonType={buttonType}
            variantAvailable={variantAvailable}
            productCartData={productCartData}
            product={shopify}
            isDigital={isDigital}
            setIsCtaVisible={setIsCtaVisible}
            productMetafields={productMetafields}
            sku={sku}
            variantSku={variantSku}
            priceTagWidth={priceTagWidth}
            gtmData={gtmData}
            trackingListName={trackingListName}
            productCategoryHierarchy={Display?.CategoryHierarchy}
            quantity={quantity}
            checkAvailabilityAction={checkAvailabilityAction}
          />

          {hasPerksPromotion || isCouponSurfacingEnabled ? (
            <PerksPromoAlert
              isLoggedIn={isLoggedIn}
              isPerksMember={isPerks}
              isEarlyAccess={isEarlyAccess}
              isExclusiveDeal={isExclusiveDeal}
              messages={window.Jbhifi?.perksPromoAlerts}
              isCouponSurfacingEnabled={isCouponSurfacingEnabled}
              onLogin={() => handleTrackClickCta('Perks login')}
              b2cBaseUrl={serviceHelper.b2cBaseUrl}
            />
          ) : null}
        </>
      ) : null}

      <StickyCallToAction
        price={price}
        buttonType={buttonType}
        productCartData={productCartData}
        product={shopify}
        gtmProduct={window?.gtm?.product ? window?.gtm?.product : shopify}
        ticket={shouldShowWasPrice || displayPriceAfterCashback ? wasPrice : 0}
        cashback={displayPriceAfterCashback}
        deliveryTag={deliveryTag}
        sku={sku}
        customerWishlist={customerWishlist}
        onlineValue={productMetafields}
        gtmData={gtmData}
        variantAvailable={variantAvailable}
        openAvailabilityDrawer={isAvailabilityWrapperOpen}
        unavailableItemLabel={getItemLabel()}
        isLoggedIn={isLoggedIn}
        isEarlyAccess={isEarlyAccess}
        isExclusiveDeal={isExclusiveDeal}
        isPerksMember={isPerks}
        trackingListName={trackingListName}
        hasPerksPromotion={hasPerksPromotion}
        productCategoryHierarchy={Display?.CategoryHierarchy}
        pricing={productPrice}
        showPerksPrice={ShowPerksPrice}
        shouldShowWasPrice={shouldShowWasPrice}
        isCtaVisible={isCtaVisible}
        toolTips={disclaimers}
        checkAvailabilityAction={checkAvailabilityAction}
        bannerLabel={priceTagBannerLabel}
      />

      {isMobile && shouldShowAskForADeal && askForADealPosition === 'top' ? (
        <AskForADeal shouldShowSalesLineNumber={shouldShowSalesLineNumber} />
      ) : null}

      {isMobile && showBnpl && price ? <BuyNowPayLater options={bnplOptions} productPrice={price} /> : null}
      {isMobile && !hasPerksPromotion && isCouponSurfacingEnabled ? (
        <PerksPromoAlert
          isLoggedIn={isLoggedIn}
          isPerksMember={isPerks}
          isEarlyAccess={isEarlyAccess}
          isExclusiveDeal={isExclusiveDeal}
          messages={window.Jbhifi?.perksPromoAlerts}
          isCouponSurfacingEnabled={isCouponSurfacingEnabled}
          onLogin={() => handleTrackClickCta('Perks login')}
          b2cBaseUrl={serviceHelper.b2cBaseUrl}
        />
      ) : null}

      <InjectedCustomLink
        linkTitle="Check your store"
        elementId="availability-button-trigger"
        clickEvent={checkAvailabilityAction}
      />
      <Messages messages={productAlerts} className={styles.messages} />
      <ProductMessages messages={productMessages} allowedTypes={['Warning']} allowedImages={false} />

      {isDesktop && shouldShowAskForADeal && askForADealPosition == 'top' ? (
        <AskForADeal shouldShowSalesLineNumber={shouldShowSalesLineNumber} />
      ) : null}

      {shouldShowCoupons ? (
        <div className={styles.coupons}>
          <CouponsContainer
            variant="mini"
            title={DEFAULT_CONTAINER_TITLE}
            coupons={couponsWithExclusions}
            onApplyCoupon={handleSelectCoupon}
            shouldShowSubheading
            shouldDisplayStatus={false}
          />
        </div>
      ) : null}

      {shouldShowMovieRating ? (
        <MovieRating
          description={movieRating.description}
          icon={movieRating.icon}
          consumerAdvice={consumerAdvice}
          releaseDateFormatted={releaseDateFormatted}
          isReleaseDateInPast={isReleaseDateInPast}
        />
      ) : (
        <>
          <KeyFeatures
            features={features}
            highlight={highlight}
            requiresExtraDescriptions={requiresExtraDescriptions}
          />
          {isSoftware(Department?.Id) && (
            <ExtraDescription
              highlight={highlight}
              softwareFormat={Department.Id === 100 ? softwareFormat : undefined}
              consumerAdvice={consumerAdvice}
              releaseDateFormatted={releaseDateFormatted}
              ageRating={ageRating}
              isReleaseDateInPast={isReleaseDateInPast}
              requiresExtraDescriptions={requiresExtraDescriptions}
              isPreOrder={isPreOrder}
            />
          )}
        </>
      )}

      {isPreOrder ? (
        <>
          <ReleaseDate preOrderDeliveryMessage={preOrderDeliveryMessage} releaseDateDisplay={releaseDateDisplay} />
          {preOrderAlert ? (
            <MessageItem message={preOrderAlert as Message} inline showIcon={false} className={styles.preOrderAlert} />
          ) : null}
        </>
      ) : null}

      <AccordionLinks
        hasProductOverview={hasProductOverview}
        hasTermsOfService={hasTermsOfService}
        shouldShowDimensions={shouldShowDimensions}
      />

      {shouldAvailabilityRender ? (
        <ProductFulfilmentInfoWrapped
          setIsAvailabilityWrapperOpen={() => setIsAvailabilityWrapperOpen(true)}
          setActiveAvailabilityVariant={variant => {
            setActiveAvailabilityVariant(variant);
          }}
          isLIA={Boolean(storeId)}
          LIAStore={LIAStore}
          shouldShowDisplayModelStatus={shouldShowDisplayModelStatus}
        />
      ) : null}

      {!isComingSoon && !isUnavailable && !lrpExists ? (
        <AvailabilityStatus
          preOrder={isPreOrder}
          isDigital={isDigital}
          digitalDeliveryPreOrderMsg={window?.Jbhifi?.pdpAlerts?.digitalDeliveryPreOrder || null}
          digitalDeliveryMsg={window?.Jbhifi?.pdpAlerts?.digitalDelivery || null}
        />
      ) : null}

      {!isComingSoon ? (
        <>
          {!isPdpLocalisationEnabled &&
          (isInStoreOnly || (buttonType !== 'DoNotDisplay' && variantAvailable && !isDigital)) ? (
            <div
              id={checkAvailabilityId}
              className={styles.availWishlistContainer({
                wishlistButtonVariant,
                doNotDisplayAndInStoreOnly,
                doNotDisplayAndNotInStoreOnly,
              })}
            >
              <JBButton
                type="secondary"
                label="Check availability"
                fullWidth
                onClick={() => setIsAvailabilityWrapperOpen(true)}
                icon={<LocationSvg />}
                iconPosition="left"
              />
            </div>
          ) : null}

          <AvailabilityWrapper
            variant={activeAvailabilityVariant}
            items={[
              {
                SKU: sku.toString(),
                Quantity: 1,
                LineItemFreightPrice: freightPrice,
                GiftCardType: undefined,
                TieredIds: marketplaceSeller?.tieredIds?.length ? marketplaceSeller?.tieredIds : undefined,
              },
            ]}
            maxDistance={window.Jbhifi?.pdpAlerts?.availabilityMaxDistance}
            isLIA={Boolean(storeId)}
            LIAStore={LIAStore}
            isOpen={isAvailabilityWrapperOpen}
            onClose={() => setIsAvailabilityWrapperOpen(false)}
            shouldShowDisplayModelStatus={shouldShowDisplayModelStatus}
            isPreOrder={isPreOrder}
            isDeliveryOnly={availability?.DeliveryOnly}
            isInStoreOnly={isInStoreOnly}
            productGroup={ProductGroup?.Description}
          />
        </>
      ) : null}

      {isServicesEnabled && !isServicesPDPHidden && serviceSuggestions ? (
        <ProductService
          shouldShowOffering={currentlyAvailable}
          variant="pdp"
          productSku={sku.toString()}
          gtmData={gtmData}
          suggestions={serviceSuggestions}
        />
      ) : null}
      {installationMessages.length ? (
        <InstallationOffer
          messages={installationMessages}
          shouldShowSalesLineNumber={shouldShowSalesLineNumber}
          isJbSalesLineEnabled={isJbSalesLineEnabled}
        />
      ) : null}

      {shouldShowMicrosoft365Attach ? <DigitalProductBlock {...ms365props} /> : null}

      {isDesktop && shouldShowAskForADeal && askForADealPosition === 'bottom' ? (
        <AskForADeal shouldShowSalesLineNumber={shouldShowSalesLineNumber} />
      ) : null}
      {!isDesktop ? (
        <>
          {shouldShowAskForADeal && askForADealPosition === 'bottom' ? (
            <AskForADeal shouldShowSalesLineNumber={shouldShowSalesLineNumber} />
          ) : null}
          {specialOffers.length > 0 ? <SpecialOffers offers={specialOffers} /> : null}

          <div className={styles.bundleContainer} data-testid="pdp-top-smart-module-container">
            <ProductBundle>
              {pdpTopSmartModuleSettings.isEnabled && !isMarketplaceProduct ? (
                <SmartModuleWrapper
                  settings={pdpTopSmartModuleSettings}
                  location="pdpTop"
                  isMarketplaceProduct={isMarketplaceProduct}
                />
              ) : null}
            </ProductBundle>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default RightSide;
